/**
 * ServiceJumpNav — slim horizontal jump bar placed at the top AND bottom
 * of every service detail page. Lets the visitor hop directly between
 * JapanETCcard, JapanTollReceipts, and JapanChangeMoney without having
 * to bounce back to the Services hub.
 *
 * Active service is highlighted in its brand color; inactive services
 * show as subtle ghost buttons that highlight on hover.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

import { SERVICE_CONTENT } from "@/data/serviceContent";

const ORDER = ["japanetccard", "japantollreceipts", "japanchangemoney"];

export default function ServiceJumpNav({ activeSlug, lang, variant = "top" }) {
  const isJa = lang === "ja";
  const isTop = variant === "top";
  return (
    <nav
      aria-label={isJa ? "サービス切り替え" : "Switch service"}
      data-testid={`service-jump-nav-${variant}`}
      className={
        isTop
          ? "sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]"
          : "bg-[#F8F9FA] border-t border-[#E2E8F0]"
      }
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`flex items-center gap-2 md:gap-4 ${isTop ? "py-2.5" : "py-5 md:py-6"} overflow-x-auto no-scrollbar`}>
          <span className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold text-[#64748B] shrink-0 hidden sm:inline-flex items-center gap-1.5">
            <ArrowRight className="h-3 w-3" strokeWidth={2.25} />
            {isJa ? "他のサービス" : "Other services"}
          </span>
          <ul className="flex items-center gap-1.5 md:gap-2 flex-1 min-w-0">
            {ORDER.map((slug) => {
              const data = SERVICE_CONTENT[slug];
              const isActive = slug === activeSlug;
              const isPlanned = slug === "japanchangemoney";
              return (
                <li key={slug}>
                  <Link
                    to={`/${lang}/services/${slug}`}
                    data-testid={`service-jump-${variant}-${slug}`}
                    aria-current={isActive ? "page" : undefined}
                    className={`group inline-flex items-center gap-2 px-3 md:px-4 ${isTop ? "h-11 md:h-9" : "h-11"} rounded-full text-[12px] md:text-[12.5px] font-semibold tracking-tight transition-all whitespace-nowrap ${
                      isActive
                        ? "text-white shadow-sm"
                        : "bg-white text-[#0F1D2D] hover:-translate-y-0.5 border border-[#E2E8F0] hover:border-transparent hover:shadow-[0_8px_18px_-10px_rgba(15,29,45,0.25)]"
                    }`}
                    style={isActive ? { backgroundColor: data.accent } : undefined}
                  >
                    {isActive && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                    <span
                      aria-hidden
                      className={`h-1.5 w-1.5 rounded-full shrink-0 ${isActive ? "hidden" : ""}`}
                      style={{ backgroundColor: data.accent }}
                    />
                    <span>{data.domain}</span>
                    {isPlanned && (
                      <span
                        className={`hidden md:inline-block text-[9px] tracking-[0.18em] font-mono uppercase font-bold ${isActive ? "text-white/80" : "text-[#94A3B8]"}`}
                      >
                        {isJa ? "準備中" : "Planned"}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}
